import React, { useState, useEffect, useRef, memo } from 'react';

import axios from 'axios';
import ChatInterface from './ChatInterface';
import { iconsPath, imagePath } from '../../utils/imagePath';
import { useSelector } from 'react-redux';
import store from '../../redux/store';
import { toggleChatInterface } from '../../redux/Slice/homeSlice';

const ChatInput = () => {
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [experienceId, setExperienceId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [micPermissionGranted, setMicPermissionGranted] = useState(false);
  const textareaRef = useRef(null);
  const chatInputRef = useRef(null);

  const { showChatInterface } = useSelector((state) => state.data);

  // Check microphone permission status
  const checkMicPermission = async () => {
    try {
      const permissionStatus = await navigator.permissions.query({
        name: 'microphone',
      });
      setMicPermissionGranted(permissionStatus.state === 'granted');
      permissionStatus.onchange = () => {
        setMicPermissionGranted(permissionStatus.state === 'granted');
        if (permissionStatus.state !== 'granted' && isListening) {
          stopListening();
          alert(
            'Microphone permission was revoked. Please enable it to continue using voice input.'
          );
        }
      };
      return permissionStatus.state === 'granted';
    } catch (error) {
      console.error('Error checking microphone permission:', error);
      return false;
    }
  };

  // Check for available audio input devices
  const checkActiveStreams = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputDevices = devices.filter(
        (device) => device.kind === 'audioinput'
      );
      if (!audioInputDevices.length) {
        alert('No audio input devices found. Please connect a microphone.');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error checking audio devices:', error);
      return false;
    }
  };

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition =
        window.webkitSpeechRecognition || window.SpeechRecognition;
      const recognitionInstance = new SpeechRecognition();

      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';

      recognitionInstance.onstart = () => {
        setIsListening(true);
      };

      recognitionInstance.onresult = (event) => {
        let finalTranscript = query;
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript = (query + ' ' + transcript).trim();
          } else {
            interimTranscript = transcript;
          }
        }

        setQuery(finalTranscript || interimTranscript);
      };

      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          alert('Microphone access is required to use voice input.');
          setMicPermissionGranted(false);
        } else if (event.error === 'no-speech') {
        } else if (event.error === 'network') {
          alert('Network error occurred during speech recognition.');
        }
      };

      recognitionInstance.onend = () => {
        setIsListening(false);
      };

      setRecognition(recognitionInstance);
      checkMicPermission();

      return () => {
        if (recognitionInstance) {
          recognitionInstance.stop();
          recognitionInstance.abort();
          setIsListening(false);
        }
      };
    } else {
      console.warn('Speech recognition not supported in this browser');
      setRecognition(null);
    }
  }, []);

  // Handle click outside and route changes to stop microphone
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        chatInputRef.current &&
        !chatInputRef.current.contains(event.target)
      ) {
        if (isListening) {
          stopListening();
        }
      }
    };

    const handleLinkClick = (event) => {
      if (event.target.tagName === 'A' || event.target.closest('a')) {
        if (isListening) {
          stopListening();
        }
      }
    };

    const handleRouteChange = () => {
      if (isListening) {
        stopListening();
      }
    };

    if (isListening) {
      document.addEventListener('click', handleClickOutside, true);
      document.addEventListener('click', handleLinkClick, true);
      window.addEventListener('beforeunload', handleRouteChange);
      window.addEventListener('popstate', handleRouteChange);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('click', handleLinkClick, true);
      window.removeEventListener('beforeunload', handleRouteChange);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [isListening]);

  const stopListening = () => {
    if (recognition && isListening) {
      try {
        recognition.stop();
        recognition.abort();
        setIsListening(false);
      } catch (error) {
        console.error('Error stopping recognition:', error);
        setIsListening(false);
      }
    }
  };

  const toggleListening = async () => {
    if (!recognition) {
      alert('Speech recognition is not supported in your browser.');
      return;
    }

    if (isListening) {
      stopListening();
      return;
    }

    try {
      const hasDevices = await checkActiveStreams();
      if (!hasDevices) return;

      const hasPermission = await checkMicPermission();
      if (!hasPermission) {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        stream.getTracks().forEach((track) => track.stop());
        setMicPermissionGranted(true);
      }

      store.dispatch(toggleChatInterface(true));
      setExperienceId(null);
      setMessages([]);
      setSelectedThreadId(null);

      recognition.start();
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      setIsListening(false);
      if (error.name === 'NotAllowedError') {
        alert(
          'Microphone access is required. Please allow microphone access and try again.'
        );
      } else if (error.name === 'NotFoundError') {
        alert(
          'No microphone found. Please connect a microphone and try again.'
        );
      } else {
        alert('Error accessing microphone. Please try again.');
      }
    }
  };

  const handleSendQuery = async () => {
    if (!query.trim()) return;

    stopListening();

    const newMessage = { id: Date.now(), text: query, sender: 'user' };
    setMessages((prevMessages) => [...prevMessages, newMessage]);
    store.dispatch(toggleChatInterface(true));

    const currentQuery = query;
    setQuery('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_IMAGE_BASE_URL}/query`,
        {
          query: currentQuery,
          ...(selectedThreadId && { threadId: selectedThreadId }),
          ...(experienceId && { experienceId }),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setSelectedThreadId(res.data.threadId);

      setMessages((prevMessages) => [
        ...prevMessages,
        { id: Date.now(), text: res.data.response, sender: 'ai' },
      ]);
    } catch (error) {
      console.error(error);
      setMessages((prevMessages) => [
        ...prevMessages,
        { id: Date.now(), text: `❌ Error: ${error.message}`, sender: 'ai' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      stopListening();
      handleSendQuery();
    }
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);

    if (isListening && e.target.value !== query) {
      stopListening();
    }

    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  return (
    <div
      ref={chatInputRef}
      className="rounded-xl flex flex-col gap-6 w-full mx-auto "
    >
      {!showChatInterface && (
        <div
          style={{ boxShadow: '0px 0px 6px rgba(0,0,0,0.45)' }}
          className="bg-[#1E1F22] p-4 flex flex-col rounded-[30px] gap-4"
        >
          <div className="flex-1 flex-wrap">
            <textarea
              ref={textareaRef}
              value={query}
              onKeyDown={handleKeyDown}
              onChange={handleInputChange}
              onFocus={() => {
                stopListening();
                store.dispatch(toggleChatInterface(true));
                setExperienceId(null);
                setMessages([]);
                setSelectedThreadId(null);
              }}
              placeholder="Search, add, generate or ask anything"
              rows={1}
              className="bg-transparent w-full resize-none overflow-hidden text-sm text-white px-4 py-2 focus:outline-none placeholder:text-[#9E9D9D]"
              style={{ minHeight: '40px', maxHeight: '200px' }}
            />
          </div>
          <div className="flex justify-between items-center">
            {/* Left: Plus and Tools buttons */}
            {/* <div className="flex pl-4 items-center gap-2">
              <button
                style={{ boxShadow: '0px 0px 6px rgba(0,0,0,0.45)' }}
                className="bg-[#28292B] p-2 rounded-md text-white hover:bg-[#3a3a3a]"
              >
                <img src={imagePath?.group96Image} alt="Plus icon" />
              </button>
              <button
                style={{ boxShadow: '0px 0px 6px rgba(0,0,0,0.45)' }}
                className="bg-[#28292B] flex items-center gap-2 text-white text-sm px-3 py-1.5 rounded-md hover:bg-[#3a3a3a]"
              >
                <img
                  className="h-4 w-4"
                  src={iconsPath?.mcpTool}
                  alt="Tools icon"
                />
                Tools
              </button>
            </div> */}

            {/* Right: Mic and Send buttons */}
            <div className="flex ml-auto gap-6">
              <button
                style={{ boxShadow: '0px 0px 6px rgba(0,0,0,0.45)' }}
                onClick={toggleListening}
                disabled={loading || !recognition}
                className={`text-white p-2 rounded-full transition-colors duration-200 ${
                  isListening
                    ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                    : 'bg-[#28292B] hover:bg-[#3a3a3a]'
                } ${loading || !recognition ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={isListening ? 'Stop listening' : 'Start voice input'}
              >
                <img
                  className="h-4 w-4"
                  src={iconsPath?.mcpSpeaker}
                  alt="Microphone"
                />
              </button>
              {query.trim() && (
                <button
                  style={{ boxShadow: '0px 0px 6px rgba(0,0,0,0.45)' }}
                  onClick={handleSendQuery}
                  disabled={loading}
                  className={`bg-[#28292B] p-2 rounded-full hover:bg-red-600 transition-colors duration-200 ${
                    loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title="Send message"
                >
                  <img
                    className="h-4 w-4"
                    src={iconsPath?.mcpArrow}
                    alt="Send"
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {showChatInterface && (
        <ChatInterface
          selectedThreadId={selectedThreadId}
          setSelectedThreadId={setSelectedThreadId}
          messages={messages}
          setMessages={setMessages}
          experienceId={experienceId}
          setExperienceId={setExperienceId}
          query={query}
          handleKeyDown={handleKeyDown}
          toggleListening={toggleListening}
          isListening={isListening}
          setQuery={setQuery}
          handleSendQuery={handleSendQuery}
          stopListening={stopListening}
        />
      )}
    </div>
  );
};

export default memo(ChatInput);
